import React, { useEffect } from "react";
import { useGetProjectInterfacesQuery } from "../store/slices/settingsSlice";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import ProjectStartForm from "../Builder/Components/sidebar/ProjectStartForm";
import { Label } from "recharts";
import HelpButton from "../HelpContent/HelpButton";

function camelCaseToWords(s: string) {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
}

const ProjectCard = ({ project }: { project: any }) => (
  <div className="p-2">
    <h5 className="">Name: {project?.Label}</h5>
    <div className="my-2 g-1">
      <div className="">
        Description:
        {project?.Description}
      </div>

      <div className="">
        Status: {project?.Status}
        {project?.Status !== "production" && (
          <span>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-danger ms-2"
            />{" "}
            This project will not run unless its status is set to "production"
          </span>
        )}
      </div>
    </div>
  </div>
);

export default function ComponentSelector({
  interfaceType = "ping-monitor",
  onSelect,
  selected,
}: {
  interfaceType?: "ping-monitor";
  onSelect?: (TriggerMonitorVariables: {
    [projectUuid: string]: {
      [fieldName: string]: {
        Kind: string;
        Values?: string[];
      };
    };
  }) => void;
  selected?: { [projectUuid: string]: any };
}) {
  const {
    data: { interface: connectionInterface, projects, total } = {
      interface: {},
      projects: [],
      total: 0,
    },
  } = useGetProjectInterfacesQuery({
    for: interfaceType,
  });

  const selectedProject = projects?.find((p) => p.UUID in (selected || {}));

  const matchVariables = (projectUUID: any) => {
    if (!connectionInterface) {
      return;
    }
    const project = projects?.find((p) => p.UUID === projectUUID);
    const projectVars: any = {};
    Object.entries(connectionInterface || {}).forEach(([fieldName, field]) => {
      const matchedVar = project?.InputArguments?.find(
        (variable: any) =>
          variable.Label.replace(/ /g, "").toUpperCase() ===
            field.Name.replace(/ /g, "").toUpperCase() &&
          variable.Format === field.Format &&
          !projectVars[variable.Field]
      );
      if (matchedVar) {
        projectVars[matchedVar.Field] = {
          Kind: fieldName,
        };
      }
    });
    return projectVars;
  };

  return (
    <div>
      <h5>
        Select a component that matches the interface of this monitor{" "}
        <HelpButton helpKey="select-project-interface" />
      </h5>
      <div className="ms-1">
        This monitor is providing the {interfaceType} interface with the
        following fields.
        <br />
        Please select or create a component that has variables matching these
        fields:
      </div>
      <div className="mb-3 mt-2 ms-1">
        {Object.entries(connectionInterface || {})
          .slice()
          .sort((a, b) => a[1].Order - b[1].Order)
          .map(([fieldName, field]) => (
            <div key={fieldName}>
              <span className="fw-bold">{camelCaseToWords(fieldName)}:</span>
              <span className="ms-2 text-secondary">{field.Description}</span>
              <span className=" ms-2">({field.Format})</span>
              {field.Required ? (
                <span className="badge bg-secondary ms-3">required</span>
              ) : (
                ""
              )}
            </div>
          ))}
      </div>
      <Input
        key={projects?.length}
        inputType="select"
        value={[selectedProject ? selectedProject.UUID : ""]}
        options={projects?.map((project) => ({
          label: project.Label,
          value: project.UUID,
        }))}
        onChange={(values: any) => {
          if (onSelect) {
            if (values.length === 0) {
              onSelect({});
              return;
            }
            onSelect({
              [values[0]]: matchVariables(values[0]),
            });
          }
        }}
        formatOptionLabel={(option: any) => {
          const project = projects?.find((p) => p.UUID === option.value);
          return project ? <ProjectCard project={project} /> : option.label;
        }}
        isClearable
      />
      {selectedProject && (
        <>
          <h5 className="mt-5">Match interface fields to variables</h5>
          {Object.keys(selected || {}).length > 0 &&
            Object.entries(selected || {}).map(
              ([projectUuid, projectVariables]) => (
                <div key={projectUuid} className="mb-2">
                  {Object.entries(connectionInterface || {})
                    .slice()
                    .sort((a, b) => a[1].Order - b[1].Order)
                    .map(([fieldName, field]) => {
                      const value = Object.entries(projectVariables).find(
                        ([k, v]: [string, any]) => v.Kind === fieldName
                      )?.[0];
                      return (
                        <div key={fieldName} className="mb-4">
                          <div className="mb-2">
                            <span>{camelCaseToWords(fieldName)}</span>
                            <span className="ms-3 text-secondary">
                              {field.Description}
                            </span>
                            {field.Required ? (
                              <span className="badge bg-secondary ms-3">
                                required
                              </span>
                            ) : (
                              ""
                            )}
                          </div>
                          <Input
                            key={fieldName}
                            inputType="select"
                            isClearable
                            value={[value]}
                            options={
                              selectedProject?.InputArguments?.filter(
                                (variable: any) =>
                                  variable.Format === field.Format
                              ).map((variable: any) => ({
                                label: variable.Label,
                                value: variable.Field,
                              })) || []
                            }
                            filterOption={
                              ((option: any, input: string) =>
                                !(
                                  (selected?.[projectUuid]?.[option.value] &&
                                    selected?.[projectUuid]?.[option.value]
                                      ?.Kind !== "user") ||
                                  !!selected?.[projectUuid]?.[option.value]
                                    ?.Values[0]
                                )) as any
                            }
                            onChange={(values: any) => {
                              if (onSelect) {
                                // Clear selection
                                const updatedProjectVars = {
                                  ...(selected?.[projectUuid] || {}),
                                };
                                const k = Object.entries(
                                  updatedProjectVars
                                ).find(
                                  ([_, v]: [string, any]) =>
                                    v.Kind === fieldName
                                )?.[0];
                                if (!!k) {
                                  delete updatedProjectVars[k];
                                }
                                if (values.length === 0) {
                                  onSelect({
                                    ...(selected || {}),
                                    [projectUuid]: updatedProjectVars,
                                  });
                                  return;
                                }
                                updatedProjectVars[values[0]] = {
                                  Kind: fieldName,
                                };
                                onSelect({
                                  ...(selected || {}),
                                  [projectUuid]: updatedProjectVars,
                                });
                              }
                            }}
                          />
                          {field.Required && !value && (
                            <div className="alert alert-warning p-1 mt-1">
                              This field is required
                            </div>
                          )}
                        </div>
                      );
                    })}

                  <h5 className="mt-5">
                    Select values for the remaining variables
                  </h5>
                  <ProjectStartForm
                    initialFormData={Object.entries(
                      selected?.[projectUuid]
                    ).reduce((acc: any, [k, v]: [string, any]) => {
                      if (v.Kind === "user") {
                        acc[k] = v.Values || [];
                      }
                      return acc;
                    }, {})}
                    filterInputArguments={(arg: any) =>
                      !(
                        selected?.[projectUuid]?.[arg.Field] &&
                        selected?.[projectUuid]?.[arg.Field]?.Kind !== "user"
                      )
                    }
                    projectID={selectedProject?.ID}
                    hideStartButton
                    showHiddenVariables
                    onChange={(arg: any) => {
                      if (onSelect) {
                        onSelect({
                          ...(selected || {}),
                          [projectUuid]: {
                            ...Object.entries(arg).reduce(
                              (acc: any, [k, v]: [string, any]) => {
                                if (acc[k]?.Kind && acc[k]?.Kind !== "user") {
                                  return acc;
                                }
                                acc[k] = { Kind: "user", Values: v };
                                return acc;
                              },
                              selected?.[projectUuid] || {}
                            ),
                          },
                        });
                      }
                    }}
                  />
                </div>
              )
            )}
        </>
      )}
    </div>
  );
}
